import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, MessageSquare, FileText, User, BookOpen, Trophy, FolderOpen, MessagesSquare, History } from "lucide-react";
import { ClientAppointments } from "@/components/client-portal/ClientAppointments";
import { ClientMessages } from "@/components/client-portal/ClientMessages";
import { ClientIntakeFormView } from "@/components/client-portal/ClientIntakeFormView";
import { ClientConsentManager } from "@/components/client-portal/ClientConsentManager";
import { ClientHomeworkView } from "@/components/client-portal/ClientHomeworkView";
import { ClientMilestonesView } from "@/components/client-portal/ClientMilestonesView";
import { ClientResourcesView } from "@/components/client-portal/ClientResourcesView";
import { ClientCheckInsView } from "@/components/client-portal/ClientCheckInsView";
import { ClientSessionHistory } from "@/components/client-portal/ClientSessionHistory";

interface ClinicianConnection {
  id: string;
  clinician_id: string;
  status: string;
  therappi_connected: boolean;
  clinician_profile?: {
    practice_name: string;
    practitioner_type: string;
    professional_title: string;
  };
}

export default function ClientPortal() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState<ClinicianConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<ClinicianConnection | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate("/auth");
          return;
        }
        setUser(user);
        await loadConnections(user.id);
      } catch (error) {
        console.error('[ClientPortal] Access check failed:', error);
        navigate("/auth");
      } finally {
        setLoading(false);
      }
    };
    checkAccess();
  }, [navigate]);

  const loadConnections = async (userId: string) => {
    const { data, error } = await supabase
      .from('clinician_clients')
      .select(`
        id,
        clinician_id,
        status,
        therappi_connected
      `)
      .eq('client_id', userId)
      .eq('status', 'active');

    if (error) {
      console.error('Error loading connections:', error);
      return;
    }

    // Fetch clinician profiles separately
    const connectionsWithProfiles: ClinicianConnection[] = [];
    for (const conn of data || []) {
      const { data: profile } = await supabase
        .from('clinician_profiles')
        .select('practice_name, practitioner_type, professional_title')
        .eq('user_id', conn.clinician_id)
        .single();

      connectionsWithProfiles.push({
        ...conn,
        clinician_profile: profile || undefined
      });
    }

    setConnections(connectionsWithProfiles);
    if (connectionsWithProfiles.length > 0) {
      setSelectedConnection(connectionsWithProfiles[0]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="min-h-screen bg-background p-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="gap-2 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <div className="flex flex-col items-center justify-center text-center max-w-md mx-auto mt-20">
          <User className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">No Active Connections</h2>
          <p className="text-muted-foreground">
            You don't have any active connections with a therapist or coach yet.
            Ask your practitioner to send you an invitation.
          </p>
        </div>
      </div>
    );
  }

  const practitionerType = selectedConnection?.clinician_profile?.practitioner_type || 'therapist';
  const practitionerLabel = practitionerType === 'coach' ? 'Coach' : 'Therapist';

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/30 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                My {practitionerLabel} Portal
              </h1>
              <p className="text-sm text-muted-foreground">
                {selectedConnection?.clinician_profile?.practice_name || `My ${practitionerLabel}`}
              </p>
            </div>
          </div>
          {connections.length > 1 && (
            <select
              value={selectedConnection?.id}
              onChange={(e) => {
                const conn = connections.find(c => c.id === e.target.value);
                if (conn) setSelectedConnection(conn);
              }}
              className="bg-card border border-border rounded-md px-3 py-2 text-sm"
            >
              {connections.map(conn => (
                <option key={conn.id} value={conn.id}>
                  {conn.clinician_profile?.practice_name || 'Practitioner'}
                </option>
              ))}
            </select>
          )}
        </div>
      </header>

      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <Tabs defaultValue="appointments" className="space-y-4 sm:space-y-6">
          <TabsList className="flex overflow-x-auto scrollbar-hide w-full gap-1 bg-card/50 p-1 justify-start">
            <TabsTrigger value="appointments" className="gap-1.5 shrink-0 px-2.5 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Appointments</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-1.5 shrink-0 px-2.5 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <History className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Sessions</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-1.5 shrink-0 px-2.5 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Messages</span>
            </TabsTrigger>
            <TabsTrigger value="homework" className="gap-1.5 shrink-0 px-2.5 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Homework</span>
            </TabsTrigger>
            <TabsTrigger value="checkins" className="gap-1.5 shrink-0 px-2.5 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <MessagesSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Check-Ins</span>
            </TabsTrigger>
            <TabsTrigger value="milestones" className="gap-1.5 shrink-0 px-2.5 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Trophy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Milestones</span>
            </TabsTrigger>
            <TabsTrigger value="resources" className="gap-1.5 shrink-0 px-2.5 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <FolderOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Resources</span>
            </TabsTrigger>
            <TabsTrigger value="intake" className="gap-1.5 shrink-0 px-2.5 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Intake</span>
            </TabsTrigger>
            <TabsTrigger value="privacy" className="gap-1.5 shrink-0 px-2.5 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <User className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Privacy</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="appointments">
            {selectedConnection && user && (
              <ClientAppointments
                clientId={user.id}
                clinicianId={selectedConnection.clinician_id}
                clinicianClientId={selectedConnection.id}
                practitionerType={practitionerType}
              />
            )}
          </TabsContent>

          <TabsContent value="history">
            {selectedConnection && user && (
              <ClientSessionHistory
                clientId={user.id}
                clinicianId={selectedConnection.clinician_id}
              />
            )}
          </TabsContent>

          <TabsContent value="messages">
            {selectedConnection && user && (
              <ClientMessages
                clientId={user.id}
                clinicianId={selectedConnection.clinician_id}
                clinicianClientId={selectedConnection.id}
                practitionerType={practitionerType}
              />
            )}
          </TabsContent>

          <TabsContent value="homework">
            {selectedConnection && user && (
              <ClientHomeworkView
                clientId={user.id}
                clinicianId={selectedConnection.clinician_id}
              />
            )}
          </TabsContent>

          <TabsContent value="checkins">
            {selectedConnection && user && (
              <ClientCheckInsView
                clientId={user.id}
                clinicianId={selectedConnection.clinician_id}
              />
            )}
          </TabsContent>

          <TabsContent value="milestones">
            {selectedConnection && user && (
              <ClientMilestonesView
                clientId={user.id}
                clinicianId={selectedConnection.clinician_id}
              />
            )}
          </TabsContent>

          <TabsContent value="resources">
            {selectedConnection && user && (
              <ClientResourcesView
                clientId={user.id}
                clinicianId={selectedConnection.clinician_id}
              />
            )}
          </TabsContent>

          <TabsContent value="intake">
            {selectedConnection && user && (
              <ClientIntakeFormView
                clientId={user.id}
                clinicianId={selectedConnection.clinician_id}
              />
            )}
          </TabsContent>

          <TabsContent value="privacy">
            {selectedConnection && (
              <ClientConsentManager clinicianId={selectedConnection.clinician_id} />
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
